import type { RoomCardsAttachment } from "../widget/room-cards";
export type StreamEvent =
  | RoomCardsAttachment
  | { type: 'token'; value: string }
  | {
      type: 'scheduling_slots';
      provider: 'CALENDLY' | 'CALCOM';
      slots: Array<{ start_time: string; end_time: string; scheduling_url: string }>;
      conversationId?: string;
    }
  | {
      type: 'calendly_slots';
      slots: Array<{ start_time: string; end_time: string; scheduling_url: string }>;
      conversationId?: string;
    }
  | { type: 'done' }
  | { type: 'error'; message: string; retryable?: boolean };

export interface StreamParams {
  agentId: string;
  conversationId: string;
  visitorId: string;
  input: string;
  roomSelection?: string;
  version?: string;
}

export class SSEStreamClient {
  private activeAbort: AbortController | undefined;
  private streamSeq = 0;

  constructor(private readonly baseUrl: string) { }

  stop(): void {
    this.activeAbort?.abort();
    this.activeAbort = undefined;
  }

async start(
  params: StreamParams,
  onEvent: (e: StreamEvent) => void
): Promise<{ ok: boolean }> {

  this.stop();
  const mySeq = ++this.streamSeq;
  const ac = new AbortController();
  this.activeAbort = ac;

  const url = new URL(`${this.baseUrl}/public/chat/conversations/${params.conversationId}/stream`);

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({content: params.input, ...(params.roomSelection ? { roomSelection: params.roomSelection } : {})}),
    signal: ac.signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    onEvent({
      type: 'error',
      message: `Stream failed: ${res.status} ${text}`,
      retryable: true,
    });
    return { ok: false };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  const emit = (e: StreamEvent) => {
    if (mySeq !== this.streamSeq) return; // stale stream guard
    onEvent(e);
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const chunk of parts) {
        const lines = chunk.split('\n');
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;

          const payload = trimmed.slice(5).trim();
          if (!payload) continue;

          try {
            const obj = JSON.parse(payload) as StreamEvent;
            emit(obj);
          } catch {
            emit({ type: 'token', value: payload });
          }
        }
      }
    }

    emit({ type: 'done' });
    return { ok: true };

  } catch (err: any) {
    if (ac.signal.aborted) return { ok: false };

    emit({
      type: 'error',
      message: err?.message ?? 'Stream error',
      retryable: true,
    });

    return { ok: false };
  }
}

}
